﻿import "regenerator-runtime/runtime";
import React, { useState, Fragment, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import * as IProduct from './Interfaces';
import { CartTotal } from "./CartTotal";
import { formatPrice } from "../../helpers/Formatters";
import { useAsync } from "../../hooks/useAsync";
import { ShopCountdown } from "./ShopCountdown";
import { syncCartsLocal, cancelOrder, syncCart, calculateTotals, getShopWindowLabel } from "./ShopFunctions";
import { CartProduct } from "./product/CartProduct";

function CartOrderConfirmation(props: { fresh: boolean; shopOpen: boolean }) {
    if (props.fresh) {
        return (<div className="d-flex align-items-center bg-white mb-3 pr-3">
            <img src="/images/farmer.png" width="165" height="165" alt="Farmer" className="m-3" />
            <div className="w-100">
                <h5>Order Confirmed!</h5>
                <p>You will receive an email confirming your order shortly. Please contact your Food Club Host if you don&apos;t.</p>
            </div>
        </div>);
    }
    else if (props.shopOpen) {
        return (<div className="d-flex align-items-center bg-white mb-3 pr-3">
            <img src="/images/farmer.png" width="165" height="165" alt="Farmer" className="m-3" />
            <div className="w-100">
                <h5>Order Confirmed</h5>
                <p>Your order has been placed. The shop is open, so you can cancel or amend your order if necessary.</p>
            </div>
        </div>);
    } else {
        return (<div className="d-flex align-items-center bg-white mb-3 pr-3">
            <img src="/images/farmer.png" width="165" height="165" alt="Farmer" className="m-3" />
            <div className="w-100">
                <h5>Order Confirmed</h5>
                <p>Your order has been placed and is being finalised by your Host.</p>
            </div>
        </div>);
    }
}

export function Cart(props: { initial: IProduct.ShopData; cartNav: HTMLElement }) {
    const now = new Date();
    const openWindows = props.initial.windows.filter(x => new Date(x.opens) <= now && new Date(x.closes) > now);

    const [carts, setCarts] = useState(props.initial.carts);
    const [shopWindow, setShopWindow] = useState(() => {
        if (carts.length > 0) {
            const freshestOpenWindow = openWindows.find(x => x.windowId == carts[0].windowId);
            return freshestOpenWindow ?? props.initial.current;
        }
    });    
    const [time, setTime] = useState(new Date());    
    const [freshOrder, setFreshOrder] = useState(false);
    const [terms, setTerms] = useState(false);

    const cart = shopWindow ? carts.find(x => x.windowId == shopWindow.windowId) : { windowId: 0, status: 'Dummy', dateOrdered: null, products: [] };
    const shopOpen = shopWindow?.opens && new Date(shopWindow.opens) <= now && shopWindow?.closes && new Date(shopWindow.closes) > now;
    const totals = calculateTotals(props.initial.products, props.initial.vatRate, props.initial.clubVatRegistered, shopWindow?.marketDayFee ?? 0, cart);

    useEffect(() => { //storage listener, receive cart modification events from other pages
        const listener = (e: StorageEvent) => {
            if (!document.hasFocus) {
                if (e.key === null) { //store cleared
                    setCarts(carts => carts.map(x => ({ ...x, products: [] })));
                }
                else if (e.key === '_fhcart') {
                    const storageCarts = JSON.parse(e.newValue) as IProduct.ShopCart[];

                    setCarts(storageCarts);
                    setFreshOrder(false);
                }
            }            
        };
        window.addEventListener('storage', listener);

        return () => { window.removeEventListener('storage', listener); };
    }, []);

    const itemCountChanged = useCallback((productId: number, variantId: number, qty: number) => {
        setCarts(carts => {
            const stateCart = carts.find(x => x.windowId === cart.windowId);
            const updatedCart = {
                ...stateCart, products: [...stateCart.products.filter(y => y.productVariantId !== variantId), { productVariantId: variantId, quantity: qty }]
            }
            const updatedCarts = carts.map(x => x === stateCart ? updatedCart : x);
            syncCart(updatedCarts, updatedCart, false);

            return updatedCarts;
        });

        setFreshOrder(false);
    }, [cart]);

    const refresh = () => {
        setTime(new Date());
    }

    const productVariants = props.initial.products.flatMap(x => x.variants.map(y => { return { product: x, variant: y } })).filter(x => !!cart.products.find(y => y.productVariantId == x.variant.id));
        
    const { execute: executeCancelOrder, status: statusCancelOrder, reset: resetCancelOrder } = useAsync(async () => {
        await cancelOrder(cart);
        const updatedCarts = carts.map(x => x.windowId != cart.windowId ? x : { ...x, status: 'Active' });
        setCarts(updatedCarts);
        setFreshOrder(false);
        syncCartsLocal(updatedCarts);
    }, false);
    const { execute: executeConfirmOrder, status: statusConfirmOrder } = useAsync(async () => {
        await syncCart(carts, cart, true);
        const updatedCarts = carts.map(x => x.windowId != cart.windowId ? x : { ...x, status: 'Ordered' });
        setCarts(updatedCarts);
        resetCancelOrder();
        setFreshOrder(true);
        syncCartsLocal(updatedCarts);
    }, false);

    return (
        <Fragment>
            <div className="row">
                <div className="col-12">
                    <div className="d-lg-flex d-block flex-row-reverse align-items-center mb-3" style={{ gridGap: '1rem' }}>
                        {openWindows.length > 1 ? <div className="window-selector"><select className="custom-select mb-1 mb-lg-0" id="chooseWindow" value={shopWindow.windowId} onChange={(e) => setShopWindow(props.initial.windows.find(x => x.windowId === parseInt(e.target.value, 10)))}>
                            {openWindows.map(x => <option value={x.windowId}>{getShopWindowLabel(x)}</option>)}
                        </select></div> : null}
                        <ShopCountdown current={shopWindow} next={props.initial.next} refresh={refresh} />
                        </div>
                    </div>
                <div className="col-12 col-md">
                    {cart.status === 'Ordered' ? <CartOrderConfirmation fresh={freshOrder} shopOpen={shopOpen} /> : null }

                    <div id="cart">
                        {productVariants.map((x) => <CartProduct key={x.variant.id} badges={props.initial.badges} product={x.product} variant={x.variant} itemCountChanged={itemCountChanged} cart={cart} shopOpen={shopOpen} loggedIn={props.initial.loggedIn} />)}
                    </div>
                    {props.initial.loggedIn ? null : <p>Can't see your cart? Login to review your items. If you aren't yet a member, contact your host to join</p>}
                </div>
                <div className="col-12 col-md-auto" id="cart-totals">
                    <div className="bg-white p-3">
                        <h4>Cart Totals</h4>
                        <table className="w-100">
                            <tbody>
                                {shopWindow?.marketDayFee ? (<Fragment>
                                    <tr>
                                        <td className="total-label pt-2 pb-2">Subtotal</td><td className="total-price pt-2 pb-2 pl-3">{formatPrice(totals.itemTotal)}</td>
                                    </tr>
                                    <tr>
                                        <td className="total-label pt-2 pb-2">Market Day Fee</td><td className="total-price pt-2 pb-2 pl-3">{formatPrice(shopWindow.marketDayFee)}</td>
                                    </tr>           
                                </Fragment>) : null}
                                <tr>
                                    <td className="total-label pt-2 pb-2">Total</td><td className="total-price pt-2 pb-2 pl-3">{formatPrice(totals.total)}</td>
                                </tr>
                            </tbody>
                        </table>
                        {cart.status === 'Active' && shopOpen ?
                            <div className="form-check md-mw-16 mt-3">
                                <input className="form-check-input" disabled={statusConfirmOrder === "pending"} type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} id="accept_terms" />
                                <label className="form-check-label d-inline" htmlFor="accept_terms">
                                    I accept the FoodHub Terms and Conditions
                                </label>&nbsp;(<a target="_blank" href="https://bit.ly/3q5dTwW">view</a>)
                            </div> : null}
                        <div className="mt-3">
                            {cart.status === 'Active' && statusConfirmOrder === "pending" ? <button disabled className="btn btn-primary w-100"><span className="d-inline-block spin">&#x21bb;</span></button> : (cart.status === 'Active' && shopOpen) ? <button disabled={!terms} className="btn btn-primary w-100" onClick={executeConfirmOrder}> Confirm Order</button> : status === 'Ordered' ? <button disabled className="btn btn-primary w-100">Order Confirmed</button> : null}
                        </div>                        
                        {cart.status === 'Ordered' && statusCancelOrder === "pending" ? <button disabled className="btn btn-link w-100">Withdrawing...</button> : (cart.status === 'Ordered' && shopOpen) ? <button className="btn btn-link text-warning w-100" onClick={executeCancelOrder}>Amend Order</button> : null}
                        {cart.status === 'Active' && statusCancelOrder === "success" ? <div className="md-mw-16 mt-3">Your order has been withdrawn, and you can now amend your cart if you wish. Your order will not be sent to the host unless you click Confirm again.</div> : null}
                    </div>
                </div>
            </div>
            {props.cartNav ? ReactDOM.createPortal(<CartTotal items={totals.items} total={totals.itemTotal} />, props.cartNav) : null}            
        </Fragment>
    );  
}